import { useIsMobile } from '@epilot/concorde-elements'
import { clsx } from '@epilot/journey-logic-commons'
import type { Layout, LayoutProps, UISchemaElement } from '@jsonforms/core'
import { JsonFormsDispatch, withJsonFormsLayoutProps } from '@jsonforms/react'
import { AnimatePresence, useReducedMotion } from 'motion/react'

import { useJourneyContext } from '../../../blocks-renderers/providers'
import type { AnimationOptions } from '../../../blocks-renderers/utils/hooks/useAnimationEffects'
import { useAnimationEffect } from '../../../blocks-renderers/utils/hooks/useAnimationEffects'
import { useStickyZone } from '../../utils/hooks/useStickyZone'
import { BlockRevealContainer, StickyZone, StepHeader } from '../components'

import classes from './TwoColumnsLayout.module.scss'

export enum MainContentCartZones {
  'Header',
  'Footer',
  'Content',
  'Side'
}

const TwoColumnsLayout = ({
  uischema,
  renderers,
  schema,
  path,
  enabled
}: LayoutProps) => {
  const { context } = useJourneyContext()

  const currentStepIndex = context._navigationInfo.currentStepIndex
  const currentStep = context.journey.steps[currentStepIndex]
  const isPreview = context?._isPreview
  const { animationType } = useAnimationEffect()
  const isAnimationActive = !isPreview
  const shouldReduceMotion = useReducedMotion()

  const animationOptions: AnimationOptions = {
    animationType,
    isAnimationActive,
    shouldReduceMotion
  }

  const layout = uischema as Layout
  /**
   * @todo layout.elements is typed as UISchemaElement[] but used as UISchemaElement[][],
   * fix this typing or refactor it.
   */
  const elements = (layout.elements as any[]) || []

  const isMobile = useIsMobile()
  const { sticky, hasShopCartSticky, stickyRef, stickyHeight } = useStickyZone(
    elements,
    isMobile
  )

  // first element is the main content
  const content = renderZone({
    elements: elements[0],
    name: 'content',
    layoutProps: {
      renderers,
      schema,
      path,
      enabled
    },
    animationOptions,
    className: classes.content,
    isMobile
  })

  // second element is the side (ex. shopping cart)
  const side = renderZone({
    elements: elements[1],
    name: 'side',
    layoutProps: {
      renderers,
      schema,
      path,
      enabled
    },
    animationOptions,
    className: classes.sidebar,
    isMobile
  })

  // third element is the header
  const header = renderZone({
    elements: elements[2],
    name: 'header',
    layoutProps: {
      renderers,
      schema,
      path,
      enabled
    },
    animationOptions,
    isMobile
  })

  // forth element is the footer
  const footer = renderZone({
    elements: elements[3],
    name: 'footer',
    layoutProps: {
      renderers,
      schema,
      path,
      enabled
    },
    animationOptions,
    isMobile
  })

  const hasRequiredField = Boolean(
    schema?.required && schema.required.length > 0
  )

  const customStyle = {
    ...(isMobile ? { paddingBottom: sticky?.length ? stickyHeight : 0 } : {})
  }

  return (
    <div
      className={clsx(
        'Concorde-Layout-Container',
        classes.twoColumnsLayoutContainer
      )}
      id={`Concorde-Step-Panel-${currentStepIndex + 1}`}
      role="tabpanel"
      style={customStyle}
    >
      <StepHeader
        className={classes.twoColumnsLayoutHeader}
        hasRequiredField={hasRequiredField}
        step={{ ...currentStep, stepIndex: currentStepIndex }}
      />

      <div
        className={clsx(
          'Concorde-Layout-Content',
          classes.twoColumnsLayoutContent
        )}
      >
        {header}
        <div className={classes.contentAndSidebar}>
          {content}
          {side}
        </div>
        {footer}
      </div>

      {isMobile && sticky.length > 0 && (
        <StickyZone
          hasShopCartSticky={hasShopCartSticky}
          ref={stickyRef}
          stickyItemCount={sticky.length}
        >
          {sticky.map((child: UISchemaElement | undefined, index: number) => (
            <JsonFormsDispatch
              enabled={enabled}
              key={'sticky' + index}
              path={path}
              renderers={renderers}
              schema={schema}
              uischema={child}
            />
          ))}
        </StickyZone>
      )}
    </div>
  )
}

interface RenderZoneProps {
  elements: UISchemaElement[]
  name: string
  layoutProps: Pick<LayoutProps, 'renderers' | 'schema' | 'path' | 'enabled'>
  animationOptions: AnimationOptions
  className?: string
  isMobile?: boolean
}

function renderZone({
  elements,
  name,
  layoutProps: { renderers, schema, path, enabled },
  animationOptions: { animationType, isAnimationActive, shouldReduceMotion },
  className,
  isMobile
}: RenderZoneProps) {
  if (elements.length > 0) {
    return (
      <div className={clsx(classes.zoneWrapper, className)}>
        <AnimatePresence
          custom={shouldReduceMotion ? 'none' : animationType}
          mode="popLayout"
        >
          {elements.map((child: UISchemaElement, index: number) => {
            // When display is undefined, it means that there's no display logic connected to it
            const isVisible =
              child?.options?.display === undefined
                ? true
                : child?.options?.display

            if (!isVisible || (child.options?.stickyOnMobile && isMobile)) {
              return null
            }

            return (
              <BlockRevealContainer
                animationType={animationType}
                blockType={child.type}
                child={child}
                isActive={isAnimationActive}
                isVisible={isVisible}
                key={name + index}
              >
                <JsonFormsDispatch
                  enabled={enabled}
                  key={name + index}
                  path={path}
                  renderers={renderers}
                  schema={schema}
                  uischema={child}
                />
              </BlockRevealContainer>
            )
          })}
        </AnimatePresence>
      </div>
    )
  }

  return <></>
}

export default withJsonFormsLayoutProps(TwoColumnsLayout)
